import { useEffect, useState } from "react";

import { contentfulClient } from "../services/api.ts";
import { useGlobalContext } from "../utils/GlobalContext.ts";

// Identifies the canonical PageMetaData entry that serves as the site-wide
// default for <title>, description and og:* tags. Per-page <Helmet> blocks
// will still override these via react-helmet-async's last-mounted-wins
// semantics.
const GLOBAL_METADATA_INTERNAL_TITLE = "MetaData - Homepage";

const GetGlobalMetadata = `
  query GetGlobalMetadata(
    $internalTitle: String!
    $locale: String
    $preview: Boolean
  ) {
    pageMetaDataCollection(
      locale: $locale
      preview: $preview
      where: { internalTitle: $internalTitle }
      limit: 1
    ) {
      items {
        pageTitle
        description
        ogImage {
          url
        }
      }
    }
  }
`;

interface GetGlobalMetadataResponse {
  pageMetaDataCollection?: {
    items: Array<{
      pageTitle?: string | null;
      description?: string | null;
      ogImage?: { url?: string | null } | null;
    } | null>;
  } | null;
}

export interface GlobalMetadata {
  title?: string;
  description?: string;
  image?: string;
}

interface UseGlobalMetadataResult {
  metadata: GlobalMetadata;
  loading: boolean;
  error: boolean;
}

export function useGlobalMetadata(): UseGlobalMetadataResult {
  const { localeConfig, previewContent } = useGlobalContext();
  const [metadata, setMetadata] = useState<GlobalMetadata>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchGlobalMetadata() {
      try {
        setLoading(true);
        setError(false);

        const response =
          await contentfulClient.query<GetGlobalMetadataResponse>(
            GetGlobalMetadata,
            {
              internalTitle: GLOBAL_METADATA_INTERNAL_TITLE,
              locale: localeConfig.currentLocale ?? undefined,
              preview: Boolean(previewContent),
            },
          );

        if (cancelled) return;

        const item = response.pageMetaDataCollection?.items?.[0];

        setMetadata({
          title: item?.pageTitle ?? undefined,
          description: item?.description ?? undefined,
          image: item?.ogImage?.url ?? undefined,
        });
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching global PageMetaData:", err);
        setError(true);
        setMetadata({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchGlobalMetadata();

    return () => {
      cancelled = true;
    };
  }, [localeConfig.currentLocale, previewContent]);

  return { metadata, loading, error };
}
